<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseCsvFile, rebuildCsvWithMappedHeaders } from 'dashboard/composables/useCsvParser';
import { useCsvColumnMapping } from 'dashboard/composables/useCsvColumnMapping';
import ContactImportColumnMapping from './contactImport/ContactImportColumnMapping.vue';

const store = useStore();
const { t } = useI18n();

const currentUser = computed(() => store.getters.getCurrentUser);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// Error log popup state
const showErrorLog = ref(false);
const selectedImportErrors = ref([]);

// Delete confirmation state
const showDeleteConfirm = ref(false);
const importToDelete = ref(null);

// Step state (1=upload, 2=mapping, 3=settings)
const currentStep = ref(1);

// CSV parsing state
const csvFile = ref(null);
const parsedCsv = ref(null);
const csvHeaders = ref([]);

// Column mapping composable
const {
  mapping: columnMapping,
  runAutoDetect,
  setMapping: updateMapping,
  isValid: isMappingValid,
  allAutoDetected,
  isAutoDetected,
} = useCsvColumnMapping(csvHeaders);

// Import settings state
const createConversations = ref(false);
const selectedLabelIds = ref([]);
const privateMessageTemplate = ref('');
const selectedInboxId = ref(null);
const selectedFunnelId = ref(null);
const selectedStageName = ref('');
const productData = ref({
  enabled: false,
  name: '',
  description: '',
  amount: '',
  currency: 'BRL',
});
const appointmentData = ref({
  enabled: false,
  title: '',
  description: '',
  scheduled_at: '',
  duration_minutes: 60,
  event_type: 'event',
  status: 'scheduled',
  location: '',
});
const notesContent = ref('');
const isProcessing = ref(false);

// Store computed
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const funnels = computed(() => store.getters['funnels/getFunnels'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const selectedFunnel = computed(() => {
  if (!selectedFunnelId.value) return null;
  return store.getters['funnels/getFunnel'](selectedFunnelId.value);
});
const availableStages = computed(() => {
  if (!selectedFunnel.value?.stages) return [];
  return Object.entries(selectedFunnel.value.stages).map(([key, stage]) => ({
    key,
    ...stage,
  }));
});
const uiFlags = computed(() => store.getters['contactImports/getUIFlags']);
const imports = computed(
  () => store.getters['contactImports/getContactImports']
);

// Step definitions for the indicator
const steps = [
  { key: 1, labelKey: 'CONTACT_IMPORT.STEPS.UPLOAD' },
  { key: 2, labelKey: 'CONTACT_IMPORT.STEPS.MAPPING' },
  { key: 3, labelKey: 'CONTACT_IMPORT.STEPS.SETTINGS' },
];

// Auto-detected headers set for the mapping component
const autoDetectedHeaders = computed(() => {
  const detected = new Set();
  csvHeaders.value.forEach(header => {
    if (isAutoDetected(header)) detected.add(header);
  });
  return detected;
});

// File upload handler
const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  const isCSV =
    file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
  if (!isCSV) {
    useAlert(t('CONTACT_IMPORT.ERRORS.INVALID_FILE_TYPE'));
    event.target.value = '';
    return;
  }

  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert(t('CONTACT_IMPORT.ERRORS.FILE_TOO_LARGE'));
    event.target.value = '';
    return;
  }

  csvFile.value = file;

  try {
    const result = await parseCsvFile(file);
    parsedCsv.value = result;
    csvHeaders.value = result.headers;

    if (result.headers.length === 0) {
      useAlert(t('CONTACT_IMPORT.ERRORS.EMPTY_CSV'));
      csvFile.value = null;
      return;
    }

    // Auto-detect column mapping
    runAutoDetect();

    // Go to mapping step
    currentStep.value = 2;
  } catch (error) {
    useAlert(t('CONTACT_IMPORT.ERRORS.PARSE_FAILED'));
    csvFile.value = null;
  }
};

const handleMappingUpdate = newMapping => {
  columnMapping.value = newMapping;
};

const downloadTemplate = async () => {
  try {
    await store.dispatch('contactImports/downloadTemplate');
    useAlert(t('CONTACT_IMPORT.TEMPLATE_DOWNLOADED'));
  } catch (error) {
    useAlert(t('CONTACT_IMPORT.ERRORS.DOWNLOAD_FAILED'));
  }
};

const goToStep = step => {
  if (step === 3 && !isMappingValid.value) {
    useAlert(t('CONTACT_IMPORT.MAPPING.VALIDATION_ERROR'));
    return;
  }
  currentStep.value = step;
};

const validateSettings = () => {
  if (createConversations.value) {
    if (!privateMessageTemplate.value.trim()) {
      useAlert(t('CONTACT_IMPORT.ERRORS.MESSAGE_REQUIRED'));
      return false;
    }
    if (!selectedInboxId.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.INBOX_REQUIRED'));
      return false;
    }
    if (!selectedFunnelId.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.FUNNEL_REQUIRED'));
      return false;
    }
    if (!selectedStageName.value) {
      useAlert(t('CONTACT_IMPORT.ERRORS.STAGE_REQUIRED'));
      return false;
    }
  }
  return true;
};

const toggleLabel = labelId => {
  const index = selectedLabelIds.value.indexOf(labelId);
  if (index === -1) {
    selectedLabelIds.value.push(labelId);
  } else {
    selectedLabelIds.value.splice(index, 1);
  }
};

const resetForm = () => {
  currentStep.value = 1;
  csvFile.value = null;
  parsedCsv.value = null;
  csvHeaders.value = [];
  createConversations.value = false;
  selectedLabelIds.value = [];
  privateMessageTemplate.value = '';
  selectedInboxId.value = null;
  selectedFunnelId.value = null;
  selectedStageName.value = '';
  productData.value = {
    enabled: false,
    name: '',
    description: '',
    amount: '',
    currency: 'BRL',
  };
  appointmentData.value = {
    enabled: false,
    title: '',
    description: '',
    scheduled_at: '',
    duration_minutes: 60,
    event_type: 'event',
    status: 'scheduled',
    location: '',
  };
  notesContent.value = '';
  const fileInput = document.getElementById('csv-file-input');
  if (fileInput) fileInput.value = '';
};

const submitImport = async () => {
  if (!validateSettings()) return;

  isProcessing.value = true;

  try {
    // Rebuild CSV with mapped headers
    const mappedCsvContent = rebuildCsvWithMappedHeaders(
      parsedCsv.value.rawText,
      columnMapping.value,
      parsedCsv.value.delimiter
    );

    const importPayload = {
      contact_import: {
        create_conversations: createConversations.value,
        label_ids: selectedLabelIds.value,
        inbox_id: createConversations.value ? selectedInboxId.value : null,
        funnel_id: createConversations.value ? selectedFunnelId.value : null,
        stage_name: createConversations.value ? selectedStageName.value : null,
        private_message_template: createConversations.value
          ? privateMessageTemplate.value
          : null,
        notes_content:
          createConversations.value && notesContent.value
            ? notesContent.value
            : null,
        import_data: {
          csv_data: mappedCsvContent,
        },
        product_data:
          createConversations.value && productData.value.enabled
            ? {
                name: productData.value.name,
                description: productData.value.description,
                amount: parseFloat(productData.value.amount) || 0,
                currency: productData.value.currency || 'BRL',
              }
            : null,
        appointment_data:
          createConversations.value && appointmentData.value.enabled
            ? {
                title: appointmentData.value.title,
                description: appointmentData.value.description,
                scheduled_at: appointmentData.value.scheduled_at,
                duration_minutes:
                  parseInt(appointmentData.value.duration_minutes, 10) || 60,
                event_type: appointmentData.value.event_type || 'event',
                status: appointmentData.value.status || 'scheduled',
                location: appointmentData.value.location || null,
              }
            : null,
      },
    };

    await store.dispatch('contactImports/create', importPayload);
    useAlert(t('CONTACT_IMPORT.SUCCESS'));
    resetForm();
  } catch (error) {
    let errorMessage = t('CONTACT_IMPORT.ERRORS.IMPORT_FAILED');

    if (error.response?.data) {
      const data = error.response.data;
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        [errorMessage] = data.errors;
      } else if (typeof data.errors === 'object' && data.errors !== null) {
        const firstKey = Object.keys(data.errors)[0];
        if (firstKey && Array.isArray(data.errors[firstKey])) {
          errorMessage = `${firstKey}: ${data.errors[firstKey][0]}`;
        } else if (firstKey) {
          errorMessage = `${firstKey}: ${data.errors[firstKey]}`;
        }
      } else if (data.error) {
        errorMessage = data.error;
      } else if (data.message) {
        errorMessage = data.message;
      }
    }

    useAlert(errorMessage);
  } finally {
    isProcessing.value = false;
  }
};

const getStatusLabel = status => {
  const statusKey = status.toUpperCase();
  return t(`CONTACT_IMPORT.STATUS.${statusKey}`);
};

const openErrorLog = importRecord => {
  selectedImportErrors.value = importRecord.error_log || [];
  showErrorLog.value = true;
};

const closeErrorLog = () => {
  showErrorLog.value = false;
  selectedImportErrors.value = [];
};

const confirmDelete = importRecord => {
  importToDelete.value = importRecord;
  showDeleteConfirm.value = true;
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
  importToDelete.value = null;
};

const executeDelete = async () => {
  if (!importToDelete.value) return;
  try {
    await store.dispatch('contactImports/delete', importToDelete.value.id);
    useAlert(t('CONTACT_IMPORT.DELETE_SUCCESS'));
  } catch {
    useAlert(t('CONTACT_IMPORT.DELETE_ERROR'));
  } finally {
    showDeleteConfirm.value = false;
    importToDelete.value = null;
  }
};

const formatErrorTimestamp = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const loadData = async () => {
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('funnels/get'),
      store.dispatch('contactImports/get'),
      store.dispatch('labels/get'),
    ]);
  } catch (error) {
    // Silently fail - UI will show empty state
  }
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <section
      class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-background"
    >
      <div class="flex flex-col w-full h-full transition-all duration-300">
        <!-- Header -->
        <div class="p-6 border-b border-n-slate-6">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('CONTACT_IMPORT.TITLE') }}
          </h1>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('CONTACT_IMPORT.DESCRIPTION') }}
          </p>

          <!-- Step Indicator -->
          <div class="flex items-center gap-2 mt-4">
            <template v-for="(step, index) in steps" :key="step.key">
              <!-- Step circle + label -->
              <button
                type="button"
                class="flex items-center gap-2 text-sm font-medium transition-colors"
                :class="{
                  'text-n-blue-11': currentStep === step.key,
                  'text-n-teal-11': currentStep > step.key,
                  'text-n-slate-9': currentStep < step.key,
                }"
                :disabled="step.key > currentStep"
                @click="step.key < currentStep && goToStep(step.key)"
              >
                <span
                  class="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors"
                  :class="{
                    'bg-n-blue-9 text-white': currentStep === step.key,
                    'bg-n-teal-9 text-white': currentStep > step.key,
                    'bg-n-slate-3 text-n-slate-9': currentStep < step.key,
                  }"
                >
                  <fluent-icon
                    v-if="currentStep > step.key"
                    icon="checkmark"
                    size="14"
                  />
                  <span v-else>{{ step.key }}</span>
                </span>
                <span class="hidden sm:inline">{{ t(step.labelKey) }}</span>
              </button>

              <!-- Connector line -->
              <div
                v-if="index < steps.length - 1"
                class="flex-1 h-px max-w-[60px]"
                :class="{
                  'bg-n-teal-9': currentStep > step.key,
                  'bg-n-slate-6': currentStep <= step.key,
                }"
              />
            </template>
          </div>
        </div>

        <!-- Main Content -->
        <main class="flex-1 overflow-y-auto">
          <div class="w-full mx-auto max-w-[60rem] p-6 space-y-6">
            <!-- ==================== STEP 1: Upload CSV ==================== -->
            <template v-if="currentStep === 1">
              <!-- CSV Template Download -->
              <div
                class="p-4 bg-n-blue-2 rounded-lg border border-n-blue-6"
              >
                <h3 class="text-sm font-medium text-n-blue-11 mb-2">
                  {{ t('CONTACT_IMPORT.TEMPLATE.TITLE') }}
                </h3>
                <p class="text-sm text-n-blue-11 mb-3">
                  {{ t('CONTACT_IMPORT.TEMPLATE.DESCRIPTION') }}
                </p>
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-n-blue-9 hover:bg-n-blue-10 rounded-md transition-colors"
                  @click="downloadTemplate"
                >
                  {{ t('CONTACT_IMPORT.TEMPLATE.DOWNLOAD_BUTTON') }}
                </button>
              </div>

              <!-- CSV File Upload -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.CSV_FILE') }}
                  <span class="text-n-red-9">*</span>
                </label>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-n-blue-2 file:text-n-blue-11 hover:file:bg-n-blue-3"
                  @change="handleFileUpload"
                />
                <p v-if="csvFile" class="text-sm text-n-green-9">
                  {{
                    t('CONTACT_IMPORT.FILE_SELECTED', {
                      filename: csvFile.name,
                    })
                  }}
                </p>
                <p class="text-xs text-n-slate-9">
                  {{ t('CONTACT_IMPORT.MAPPING.UPLOAD_HINT') }}
                </p>
              </div>
            </template>

            <!-- ==================== STEP 2: Map Columns ==================== -->
            <template v-if="currentStep === 2 && parsedCsv">
              <ContactImportColumnMapping
                :headers="parsedCsv.headers"
                :preview-rows="parsedCsv.rows"
                :mapping="columnMapping"
                :is-valid="isMappingValid"
                :all-auto-detected="allAutoDetected"
                :auto-detected-headers="autoDetectedHeaders"
                @update:mapping="handleMappingUpdate"
              />

              <!-- Navigation -->
              <div class="flex justify-between pt-4">
                <button
                  type="button"
                  class="px-6 py-2 text-sm font-medium text-n-slate-12 bg-n-background border border-n-slate-6 rounded-md hover:bg-n-slate-2"
                  @click="goToStep(1)"
                >
                  {{ t('CONTACT_IMPORT.NAVIGATION.BACK') }}
                </button>
                <button
                  type="button"
                  class="px-6 py-2 text-sm font-medium text-white bg-n-blue-9 rounded-md hover:bg-n-blue-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!isMappingValid"
                  @click="goToStep(3)"
                >
                  {{ t('CONTACT_IMPORT.NAVIGATION.NEXT') }}
                </button>
              </div>
            </template>

            <!-- ==================== STEP 3: Settings + Submit ==================== -->
            <template v-if="currentStep === 3">
              <!-- File summary -->
              <div
                class="p-3 bg-n-slate-2 rounded-lg border border-n-slate-6 flex items-center justify-between"
              >
                <div class="text-sm text-n-slate-12">
                  <span class="font-medium">{{ csvFile?.name }}</span>
                  <span class="text-n-slate-9 ml-2">
                    ({{ parsedCsv?.headers?.length }}
                    {{ t('CONTACT_IMPORT.MAPPING.COLUMNS') }})
                  </span>
                </div>
                <button
                  type="button"
                  class="text-xs text-n-blue-11 hover:underline"
                  @click="goToStep(2)"
                >
                  {{ t('CONTACT_IMPORT.MAPPING.EDIT_MAPPING') }}
                </button>
              </div>

              <!-- Labels Selection -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-n-slate-12">
                  {{ t('CONTACT_IMPORT.LABELS') }}
                </label>
                <p class="text-sm text-n-slate-11 mb-2">
                  {{ t('CONTACT_IMPORT.LABELS_DESCRIPTION') }}
                </p>
                <div
                  v-if="labels.length > 0"
                  class="flex flex-wrap gap-2 p-3 border border-n-slate-6 rounded-md bg-n-background"
                >
                  <button
                    v-for="label in labels"
                    :key="label.id"
                    type="button"
                    class="px-3 py-1 text-sm rounded-full border transition-colors"
                    :class="
                      selectedLabelIds.includes(label.id)
                        ? 'bg-n-blue-9 text-white border-n-blue-9'
                        : 'bg-n-background text-n-slate-12 border-n-slate-6 hover:border-n-blue-9'
                    "
                    :style="
                      selectedLabelIds.includes(label.id)
                        ? { backgroundColor: label.color }
                        : {}
                    "
                    @click="toggleLabel(label.id)"
                  >
                    {{ label.title }}
                  </button>
                </div>
                <p v-else class="text-sm text-n-slate-9 italic">
                  {{ t('CONTACT_IMPORT.NO_LABELS') }}
                </p>
              </div>

              <!-- Create Conversations Toggle -->
              <div
                class="p-4 bg-n-amber-2 rounded-lg border border-n-amber-6 space-y-3"
              >
                <div class="flex items-center">
                  <input
                    id="create-conversations"
                    v-model="createConversations"
                    type="checkbox"
                    class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                  />
                  <label
                    for="create-conversations"
                    class="ml-2 text-sm font-medium text-n-amber-11"
                  >
                    {{ t('CONTACT_IMPORT.CREATE_CONVERSATIONS') }}
                  </label>
                </div>
                <p class="text-sm text-n-amber-11">
                  {{ t('CONTACT_IMPORT.CREATE_CONVERSATIONS_DESCRIPTION') }}
                </p>
              </div>

              <!-- Conversation Settings -->
              <div v-if="createConversations" class="space-y-6">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('CONTACT_IMPORT.PRIVATE_MESSAGE') }}
                    <span class="text-n-red-9">*</span>
                  </label>
                  <textarea
                    v-model="privateMessageTemplate"
                    rows="4"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="t('CONTACT_IMPORT.MESSAGE_PLACEHOLDER')"
                  />
                </div>

                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('CONTACT_IMPORT.INBOX') }}
                    <span class="text-n-red-9">*</span>
                  </label>
                  <select
                    v-model="selectedInboxId"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                  >
                    <option :value="null">
                      {{ t('CONTACT_IMPORT.SELECT_INBOX') }}
                    </option>
                    <option
                      v-for="inbox in inboxes"
                      :key="inbox.id"
                      :value="inbox.id"
                    >
                      {{ inbox.name }}
                    </option>
                  </select>
                </div>

                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('CONTACT_IMPORT.FUNNEL') }}
                    <span class="text-n-red-9">*</span>
                  </label>
                  <select
                    v-model="selectedFunnelId"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                    @change="selectedStageName = ''"
                  >
                    <option :value="null">
                      {{ t('CONTACT_IMPORT.SELECT_FUNNEL') }}
                    </option>
                    <option
                      v-for="funnel in funnels"
                      :key="funnel.id"
                      :value="funnel.id"
                    >
                      {{ funnel.name }}
                    </option>
                  </select>
                </div>

                <div v-if="selectedFunnelId" class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('CONTACT_IMPORT.STAGE') }}
                    <span class="text-n-red-9">*</span>
                  </label>
                  <select
                    v-model="selectedStageName"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                  >
                    <option value="">
                      {{ t('CONTACT_IMPORT.SELECT_STAGE') }}
                    </option>
                    <option
                      v-for="stage in availableStages"
                      :key="stage.key"
                      :value="stage.key"
                    >
                      {{ stage.label }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Optional fields only when createConversations -->
              <template v-if="createConversations">
                <!-- Product -->
                <div
                  class="border border-n-slate-6 rounded-lg p-4 space-y-3"
                >
                  <div class="flex items-center">
                    <input
                      id="product-enabled"
                      v-model="productData.enabled"
                      type="checkbox"
                      class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                    />
                    <label
                      for="product-enabled"
                      class="ml-2 text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CONTACT_IMPORT.PRODUCT.TITLE') }}
                    </label>
                  </div>
                  <div v-if="productData.enabled" class="space-y-3 ml-6">
                    <input
                      v-model="productData.name"
                      type="text"
                      class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="t('CONTACT_IMPORT.PRODUCT.NAME')"
                    />
                    <input
                      v-model="productData.description"
                      type="text"
                      class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="t('CONTACT_IMPORT.PRODUCT.DESCRIPTION')"
                    />
                    <div class="grid grid-cols-2 gap-3">
                      <input
                        v-model="productData.amount"
                        type="number"
                        step="0.01"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                        :placeholder="
                          t('CONTACT_IMPORT.PRODUCT.VALUE_PLACEHOLDER')
                        "
                      />
                      <select
                        v-model="productData.currency"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      >
                        <option value="BRL">
                          {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_BRL') }}
                        </option>
                        <option value="USD">
                          {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_USD') }}
                        </option>
                        <option value="EUR">
                          {{ t('CONTACT_IMPORT.PRODUCT.CURRENCY_EUR') }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Appointment -->
                <div
                  class="border border-n-slate-6 rounded-lg p-4 space-y-3"
                >
                  <div class="flex items-center">
                    <input
                      id="appointment-enabled"
                      v-model="appointmentData.enabled"
                      type="checkbox"
                      class="w-4 h-4 text-n-blue-9 border-n-slate-6 rounded focus:ring-n-blue-9"
                    />
                    <label
                      for="appointment-enabled"
                      class="ml-2 text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CONTACT_IMPORT.APPOINTMENT.TITLE') }}
                    </label>
                  </div>
                  <div
                    v-if="appointmentData.enabled"
                    class="space-y-3 ml-6"
                  >
                    <input
                      v-model="appointmentData.title"
                      type="text"
                      class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="
                        t('CONTACT_IMPORT.APPOINTMENT.TITLE_PLACEHOLDER')
                      "
                    />
                    <textarea
                      v-model="appointmentData.description"
                      rows="2"
                      class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="
                        t(
                          'CONTACT_IMPORT.APPOINTMENT.DESCRIPTION_PLACEHOLDER'
                        )
                      "
                    />
                    <div class="grid grid-cols-2 gap-3">
                      <input
                        v-model="appointmentData.scheduled_at"
                        type="datetime-local"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      />
                      <input
                        v-model="appointmentData.duration_minutes"
                        type="number"
                        min="15"
                        step="15"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                        :placeholder="
                          t(
                            'CONTACT_IMPORT.APPOINTMENT.DURATION_PLACEHOLDER'
                          )
                        "
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <select
                        v-model="appointmentData.event_type"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      >
                        <option value="event">
                          {{ t('CONTACT_IMPORT.APPOINTMENT.TYPE_EVENT') }}
                        </option>
                        <option value="task">
                          {{ t('CONTACT_IMPORT.APPOINTMENT.TYPE_TASK') }}
                        </option>
                      </select>
                      <select
                        v-model="appointmentData.status"
                        class="px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      >
                        <option value="scheduled">
                          {{
                            t(
                              'CONTACT_IMPORT.APPOINTMENT.STATUS_SCHEDULED'
                            )
                          }}
                        </option>
                        <option value="confirmed">
                          {{
                            t(
                              'CONTACT_IMPORT.APPOINTMENT.STATUS_CONFIRMED'
                            )
                          }}
                        </option>
                        <option value="completed">
                          {{
                            t(
                              'CONTACT_IMPORT.APPOINTMENT.STATUS_COMPLETED'
                            )
                          }}
                        </option>
                        <option value="cancelled">
                          {{
                            t(
                              'CONTACT_IMPORT.APPOINTMENT.STATUS_CANCELLED'
                            )
                          }}
                        </option>
                        <option value="no_show">
                          {{
                            t('CONTACT_IMPORT.APPOINTMENT.STATUS_NO_SHOW')
                          }}
                        </option>
                      </select>
                    </div>
                    <input
                      v-model="appointmentData.location"
                      type="text"
                      class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 bg-n-background text-n-slate-12"
                      :placeholder="
                        t(
                          'CONTACT_IMPORT.APPOINTMENT.LOCATION_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                </div>

                <!-- Notes -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-n-slate-12">
                    {{ t('CONTACT_IMPORT.NOTES') }}
                  </label>
                  <textarea
                    v-model="notesContent"
                    rows="3"
                    class="w-full px-3 py-2 border border-n-slate-6 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 bg-n-background text-n-slate-12"
                    :placeholder="t('CONTACT_IMPORT.NOTES_PLACEHOLDER')"
                  />
                </div>
              </template>

              <!-- Submit buttons -->
              <div class="flex justify-between pt-4">
                <button
                  type="button"
                  class="px-6 py-2 text-sm font-medium text-n-slate-12 bg-n-background border border-n-slate-6 rounded-md hover:bg-n-slate-2"
                  @click="goToStep(2)"
                >
                  {{ t('CONTACT_IMPORT.NAVIGATION.BACK') }}
                </button>
                <div class="flex gap-3">
                  <button
                    type="button"
                    class="px-6 py-2 text-sm font-medium text-n-slate-12 bg-n-background border border-n-slate-6 rounded-md hover:bg-n-slate-2"
                    @click="resetForm"
                  >
                    {{ t('CONTACT_IMPORT.RESET') }}
                  </button>
                  <button
                    type="button"
                    class="px-6 py-2 text-sm font-medium text-white bg-n-blue-9 rounded-md hover:bg-n-blue-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isProcessing || uiFlags.isCreating"
                    @click="submitImport"
                  >
                    <span v-if="isProcessing || uiFlags.isCreating">
                      {{ t('CONTACT_IMPORT.PROCESSING') }}
                    </span>
                    <span v-else>
                      {{ t('CONTACT_IMPORT.SUBMIT') }}
                    </span>
                  </button>
                </div>
              </div>
            </template>

            <!-- Recent Imports (visible on all steps) -->
            <div
              v-if="imports.length > 0"
              class="mt-8 border-t border-n-slate-6 pt-6"
            >
              <h2 class="text-lg font-semibold text-n-slate-12 mb-4">
                {{ t('CONTACT_IMPORT.RECENT_IMPORTS') }}
              </h2>
              <div class="space-y-3">
                <div
                  v-for="importRecord in imports.slice(0, 5)"
                  :key="importRecord.id"
                  class="p-4 bg-n-slate-2 rounded-lg border border-n-slate-6"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-3">
                        <span
                          class="px-2 py-1 text-xs font-medium rounded-full"
                          :class="{
                            'bg-n-yellow-3 text-n-yellow-11':
                              importRecord.status === 'pending',
                            'bg-n-blue-3 text-n-blue-11':
                              importRecord.status === 'processing',
                            'bg-n-green-3 text-n-green-11':
                              importRecord.status === 'completed',
                            'bg-n-red-3 text-n-red-11':
                              importRecord.status === 'failed',
                          }"
                        >
                          {{ getStatusLabel(importRecord.status) }}
                        </span>
                        <!-- Error details button (superadmin only) -->
                        <button
                          v-if="
                            isSuperAdmin &&
                            importRecord.error_log &&
                            importRecord.error_log.length > 0
                          "
                          type="button"
                          class="px-2 py-1 text-xs font-medium rounded-full bg-n-red-3 text-n-red-11 hover:bg-n-red-4 transition-colors cursor-pointer"
                          @click="openErrorLog(importRecord)"
                        >
                          {{ importRecord.error_log.length }}
                          {{ importRecord.error_log.length === 1 ? 'erro' : 'erros' }}
                        </button>
                        <span
                          v-if="importRecord.create_conversations"
                          class="px-2 py-1 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
                        >
                          {{ t('CONTACT_IMPORT.WITH_CONVERSATIONS') }}
                        </span>
                        <span
                          v-else
                          class="px-2 py-1 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
                        >
                          {{ t('CONTACT_IMPORT.CONTACTS_ONLY') }}
                        </span>
                        <span
                          v-if="importRecord.funnel"
                          class="text-sm text-n-slate-11"
                        >
                          {{ importRecord.funnel.name }}
                          <span class="text-n-slate-9">/</span>
                          {{ importRecord.stage_name }}
                        </span>
                      </div>
                      <div class="mt-2 text-sm text-n-slate-12">
                        {{
                          t('CONTACT_IMPORT.PROGRESS', {
                            processed: importRecord.processed_contacts,
                            total: importRecord.total_contacts,
                            successful: importRecord.successful_imports,
                            failed: importRecord.failed_imports,
                          })
                        }}
                      </div>
                      <!-- Date/time of import -->
                      <div
                        v-if="importRecord.created_at"
                        class="mt-1 text-xs text-n-slate-9"
                      >
                        {{ formatErrorTimestamp(importRecord.created_at) }}
                      </div>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="text-sm font-medium text-n-slate-12">
                        {{ importRecord.progress_percentage }}<span>%</span>
                      </div>
                      <!-- Delete button (superadmin only) -->
                      <button
                        v-if="isSuperAdmin"
                        type="button"
                        class="p-1.5 text-n-slate-9 hover:text-n-red-11 hover:bg-n-red-3 rounded-md transition-colors"
                        :title="t('CONTACT_IMPORT.DELETE_IMPORT')"
                        @click="confirmDelete(importRecord)"
                      >
                        <fluent-icon icon="delete" size="16" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Log Modal -->
            <teleport to="body">
              <div
                v-if="showErrorLog"
                class="fixed inset-0 z-50 flex items-center justify-center"
              >
                <div
                  class="absolute inset-0 bg-black/50"
                  @click="closeErrorLog"
                />
                <div
                  class="relative w-full max-w-2xl max-h-[80vh] bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 flex flex-col mx-4"
                >
                  <div
                    class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700"
                  >
                    <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {{ t('CONTACT_IMPORT.ERROR_LOG_TITLE') }}
                    </h3>
                    <button
                      type="button"
                      class="p-1 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      @click="closeErrorLog"
                    >
                      <fluent-icon icon="dismiss" size="20" />
                    </button>
                  </div>
                  <div class="flex-1 overflow-y-auto p-4">
                    <div
                      v-if="selectedImportErrors.length === 0"
                      class="text-sm text-slate-500 dark:text-slate-400 text-center py-8"
                    >
                      {{ t('CONTACT_IMPORT.ERROR_LOG_EMPTY') }}
                    </div>
                    <div v-else class="space-y-2">
                      <div
                        v-for="(error, index) in selectedImportErrors"
                        :key="index"
                        class="p-3 bg-red-50 dark:bg-slate-800 border border-red-200 dark:border-slate-600 rounded-lg"
                      >
                        <div
                          v-if="error.timestamp"
                          class="text-xs text-slate-500 dark:text-slate-300 mb-1"
                        >
                          {{ formatErrorTimestamp(error.timestamp) }}
                        </div>
                        <div class="text-sm text-red-700 dark:text-white">
                          {{ error.message || error }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                    <button
                      type="button"
                      class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
                      @click="closeErrorLog"
                    >
                      {{ t('CONTACT_IMPORT.ERROR_LOG_CLOSE') }}
                    </button>
                  </div>
                </div>
              </div>
            </teleport>

            <!-- Delete Confirmation Modal -->
            <teleport to="body">
              <div
                v-if="showDeleteConfirm"
                class="fixed inset-0 z-50 flex items-center justify-center"
              >
                <div
                  class="absolute inset-0 bg-black/50"
                  @click="cancelDelete"
                />
                <div
                  class="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 mx-4"
                >
                  <div class="p-6">
                    <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      {{ t('CONTACT_IMPORT.DELETE_CONFIRM_TITLE') }}
                    </h3>
                    <p class="text-sm text-slate-600 dark:text-slate-300">
                      {{ t('CONTACT_IMPORT.DELETE_CONFIRM_MESSAGE') }}
                    </p>
                  </div>
                  <div
                    class="flex justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-700"
                  >
                    <button
                      type="button"
                      class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
                      @click="cancelDelete"
                    >
                      {{ t('CONTACT_IMPORT.DELETE_CONFIRM_NO') }}
                    </button>
                    <button
                      type="button"
                      class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                      :disabled="uiFlags.isDeleting"
                      @click="executeDelete"
                    >
                      {{ t('CONTACT_IMPORT.DELETE_CONFIRM_YES') }}
                    </button>
                  </div>
                </div>
              </div>
            </teleport>
          </div>
        </main>
      </div>
    </section>
  </div>
</template>
